import client from './client';
export const listNodes = () => client.get('/api/nodes');
export const getNode = (id: number) => client.get(`/api/nodes/${id}`);
export interface NodeAddressResolution {
  address: string;
  resolved_ip: string;
  resolved_at: string;
}
export const resolveNodeAddress = (id: number) =>
  client.get<NodeAddressResolution>(`/api/nodes/${id}/resolve-address`);
export interface NodePortSuggestion {
  port: number | null;
  source?: 'preset' | 'released';
  source_proxy_id?: number;
  source_status?: 'deleted' | 'expired';
  released_at?: string;
}
export const getNodePortSuggestion = (id: number) =>
  client.get<NodePortSuggestion>(`/api/nodes/${id}/port-suggestion`);
export const createNode = (body: { name: string; ip_public?: string; expire_at?: string; total_cost_cents?: number | null; bandwidth_down_mbps?: number | null; preset_ports?: number[] }) =>
  client.post('/api/nodes', body);
export const updateNode = (id: number, body: { ip_public?: string; expire_at?: string | null; total_cost_cents?: number | null; bandwidth_down_mbps?: number | null; preset_ports?: number[] }) =>
  client.put(`/api/nodes/${id}`, body);
export const deleteNode = (id: number) => client.delete(`/api/nodes/${id}`);
// `force` runs a disruptive action (upgrade) outside the weekly maintenance
// window; without it the server refuses with OUTSIDE_MAINTENANCE_WINDOW.
export const controlNode = (id: number, action: 'restart' | 'stop' | 'upgrade', force = false) =>
  client.post(`/api/nodes/${id}/control`, force ? { action, force: true } : { action });

// Per-row outcome of /api/nodes/batch-control. `status` is one of
// "dispatched" (WS push sent + ack waited) | "skipped" (node
// offline / not found / etc.) | "ws_failed" (push failed mid-write).
export interface BatchNodeControlResult {
  node_id: number;
  status: 'dispatched' | 'skipped' | 'ws_failed' | string;
  acked?: boolean;
  reason?: string;
  node_name?: string;
}

export interface BatchNodeControlResponse {
  ok: boolean;
  action: 'restart' | 'stop' | 'upgrade';
  dispatched: number;
  skipped: number;
  ws_failed: number;
  results: BatchNodeControlResult[];
}

// Send the same control action to many nodes in one call.
// Offline nodes are reported as skipped (not an error); the operator
// can retry once the agent reconnects. Cap 50.
export const batchControlNode = (nodeIds: number[], action: 'restart' | 'stop' | 'upgrade', force = false) =>
  client.post<BatchNodeControlResponse>('/api/nodes/batch-control', {
    node_ids: nodeIds,
    action,
    ...(force ? { force: true } : {}),
  });
// `force=true` regenerates the join_token on already-joined nodes so the
// operator can copy a fresh install command. The handler is careful to
// only touch join_token, not shared_secret, so the live agent on the
// node isn't kicked by the rotate itself. The operator must stop the
// existing agent (e.g. `systemctl stop rxjh-agent`) before running the
// returned command, otherwise the old agent re-joins with its existing
// shared_secret and the new token sits unused.
export const getInstallCommand = (id: number, force = false) =>
  client.get(`/api/nodes/${id}/install-command`, { params: force ? { force: 'true' } : {} });

export const getNodeOpsProfile = (id: number) =>
  client.get(`/api/nodes/${id}/ops-profile`);

export const updateNodeOpsProfile = (
  id: number,
  body: {
    ssh_host?: string | null;
    ssh_port?: number;
    ssh_username?: string | null;
    ssh_auth_type?: 'password' | 'private_key' | null;
    os_name?: string | null;
    os_version?: string | null;
    arch?: string | null;
    kernel_version?: string | null;
    provider?: string | null;
    region_label?: string | null;
    owner?: string | null;
    maint_window?: string | null;
  },
) => client.put(`/api/nodes/${id}/ops-profile`, body);

export const saveNodeCredential = (
  id: number,
  body: { auth_type?: 'password' | 'private_key'; password?: string; private_key?: string },
) => client.post(`/api/nodes/${id}/credentials`, body);

export const verifyNodeCredential = (id: number) =>
  client.post(`/api/nodes/${id}/credentials/verify`);

export const runNodeAction = (
  id: number,
  body: { action: 'restart_agent' | 'stop_agent' | 'start_agent' | 'upgrade_agent' | 'repair_agent' | 'restart_node' | 'run_diag'; reason?: string; force?: boolean },
) => client.post(`/api/nodes/${id}/actions`, body);

export const listNodeOperationLogs = (id: number, limit = 20) =>
  client.get(`/api/nodes/${id}/operation-logs`, { params: { limit } });
